import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Wifi, WifiOff } from "lucide-react";
import { api, WS_URL } from "@/lib/api";
import { fmtAgo } from "@/lib/format";
import { FleetMap, LivePos } from "@/components/map/FleetMap";
import { PageHeader } from "@/components/shared";
import { cn } from "@/lib/utils";

export default function LiveMap() {
  const [positions, setPositions] = useState<Record<string, LivePos>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [wsState, setWsState] = useState<"connecting" | "open" | "closed">("connecting");
  const wsRef = useRef<WebSocket | null>(null);
  const snapshot = useQuery({ queryKey: ["live"], queryFn: async () => (await api.get("/positions/live")).data as LivePos[], refetchInterval: wsState === "open" ? 60000 : 8000 });

  useEffect(() => {
    if (snapshot.data) setPositions((prev) => ({ ...prev, ...Object.fromEntries(snapshot.data.map((p) => [p.deviceId, { ...prev[p.deviceId], ...p }])) }));
  }, [snapshot.data]);

  useEffect(() => {
    const token = localStorage.getItem("tg_token");
    let ws: WebSocket;
    let timer: any;
    const connect = () => {
      ws = new WebSocket(`${WS_URL}?token=${token}`);
      wsRef.current = ws;
      ws.onopen = () => setWsState("open");
      ws.onclose = () => { setWsState("closed"); timer = setTimeout(connect, 5000); };
      ws.onerror = () => ws.close();
      ws.onmessage = (ev) => {
        const msg = JSON.parse(ev.data);
        if (msg.type === "position") setPositions((prev) => ({ ...prev, [msg.data.deviceId]: { ...prev[msg.data.deviceId], ...msg.data, online: true } }));
      };
    };
    connect();
    return () => { clearTimeout(timer); ws?.close(); };
  }, []);

  const list = Object.values(positions).sort((a, b) => (a.plate || "").localeCompare(b.plate || ""));
  return (
    <div data-testid="live-map-page" className="h-[calc(100vh-8rem)] flex flex-col">
      <PageHeader title="Live map" subtitle="Vehicle positions from the telemetry gateway" actions={
        <span data-testid="ws-status-indicator" className={cn("inline-flex items-center gap-2 font-mono text-xs rounded-full px-3 py-1 border", wsState === "open" ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : "text-amber-400 border-amber-500/30 bg-amber-500/10")}>
          {wsState === "open" ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />} {wsState === "open" ? "WebSocket live" : "Polling fallback"}
        </span>} />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 flex-1 min-h-0">
        <div className="lg:col-span-9 tg-card p-1 min-h-[400px]"><FleetMap positions={list} selectedId={selected} onSelect={setSelected} fit={list.length > 0 && !selected} /></div>
        <div className="lg:col-span-3 tg-card p-3 overflow-y-auto" data-testid="live-vehicle-list">
          <div className="tg-label mb-2 px-1">Fleet · {list.length}</div>
          {list.map((p) => (
            <button key={p.deviceId} data-testid={`live-vehicle-${p.plate}`} onClick={() => setSelected(p.vehicleId === selected ? null : p.vehicleId || null)}
              className={cn("w-full text-left rounded-md px-3 py-2 mb-1 transition-colors border", selected === p.vehicleId ? "bg-blue-500/10 border-blue-500/30" : "border-transparent hover:bg-[#1f2a3e]")}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm text-slate-100">{p.plate}</span>
                <span className={cn("h-2 w-2 rounded-full", p.online ? ((p.speed || 0) > 3 ? "bg-blue-400" : "bg-emerald-400") : "bg-slate-600")} />
              </div>
              <div className="text-[11px] text-slate-400 font-mono">{Math.round(p.speed || 0)} km/h · {fmtAgo(p.timestamp)}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
